import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  SalesDashboardService,
  SalesLead,
  LeadMetrics,
} from '../../services/sales-dashboard.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sales-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  templateUrl: './sales-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sales-dashboard.component.scss'],
})
export class SalesDashboardComponent implements OnInit, OnDestroy {
  public salesDashboardService = inject(SalesDashboardService);

  // Data properties
  leads: SalesLead[] = [];
  pagedLeads: SalesLead[] = [];
  metrics: LeadMetrics | null = null;
  selectedLead: SalesLead | null = null;
  lastUpdated: Date = new Date();

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalLeads = 0;
  totalPages = 0;

  // Filtering and sorting
  filters = {
    priority: '',
    status: '',
    dateRange: '',
    searchTerm: '',
  };

  sortColumn = 'registrationDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Expose Math to template
  Math = Math;

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initializeSubscriptions(): void {
    // Subscribe to leads updates
    const leadsSubscription = this.salesDashboardService.leads$.subscribe(
      (leads) => {
        this.leads = leads;
        this.applyFiltersAndPagination();
        this.lastUpdated = new Date();
      },
    );

    // Subscribe to metrics updates
    const metricsSubscription = this.salesDashboardService.metrics$.subscribe(
      (metrics) => {
        this.metrics = metrics;
      },
    );

    this.subscriptions.push(leadsSubscription, metricsSubscription);
  }

  private loadInitialData(): void {
    this.refreshDashboard();
  }

  /**
   * Refresh all dashboard data
   */
  refreshDashboard(): void {
    this.salesDashboardService.refreshDashboard();
  }

  /**
   * Apply filters and update displayed leads
   */
  applyFilters(): void {
    this.currentPage = 1; // Reset to first page
    this.applyFiltersAndPagination();
  }

  private applyFiltersAndPagination(): void {
    let filteredLeads = [...this.leads];

    // Apply filters
    if (this.filters.priority) {
      filteredLeads = filteredLeads.filter(
        (lead) => lead.priority === this.filters.priority,
      );
    }

    if (this.filters.status) {
      filteredLeads = filteredLeads.filter(
        (lead) => lead.status === this.filters.status,
      );
    }

    if (this.filters.searchTerm) {
      const searchTerm = this.filters.searchTerm.toLowerCase();
      filteredLeads = filteredLeads.filter(
        (lead) =>
          lead.email.toLowerCase().includes(searchTerm) ||
          lead.firstName.toLowerCase().includes(searchTerm) ||
          lead.lastName.toLowerCase().includes(searchTerm) ||
          (lead.company && lead.company.toLowerCase().includes(searchTerm)),
      );
    }

    if (this.filters.dateRange) {
      const now = new Date();
      let cutoffDate: Date;

      switch (this.filters.dateRange) {
        case 'today':
          cutoffDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
          );
          break;
        case 'week':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        default:
          cutoffDate = new Date(0);
      }

      filteredLeads = filteredLeads.filter(
        (lead) => new Date(lead.registrationDate) >= cutoffDate,
      );
    }

    // Apply sorting
    filteredLeads.sort((a, b) => {
      let aValue: any = a[this.sortColumn as keyof SalesLead];
      let bValue: any = b[this.sortColumn as keyof SalesLead];

      // Handle different data types
      if (this.sortColumn === 'registrationDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Update totals
    this.totalLeads = filteredLeads.length;
    this.totalPages = Math.ceil(this.totalLeads / this.pageSize);

    // Apply pagination
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.pagedLeads = filteredLeads.slice(startIndex, endIndex);
  }

  /**
   * Sort leads by column
   */
  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFiltersAndPagination();
  }

  /**
   * Change page
   */
  changePage(page: number, event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.applyFiltersAndPagination();
    }
  }

  /**
   * Get page numbers for pagination
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxVisiblePages / 2),
    );
    const endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  /**
   * Update lead status
   */
  updateLeadStatus(
    lead: SalesLead,
    status: SalesLead['status'],
    event: Event,
  ): void {
    event.preventDefault();

    this.salesDashboardService.updateLeadStatus(lead.id, status).subscribe({
      next: (updatedLead) => {
        // Update local lead data
        const index = this.leads.findIndex((l) => l.id === lead.id);
        if (index !== -1) {
          this.leads[index] = updatedLead;
          this.applyFiltersAndPagination();
        }
      },
      error: (error) => {
        console.error('Failed to update lead status:', error);
        // TODO: Show error toast
      },
    });
  }

  /**
   * View lead details
   */
  viewLeadDetails(lead: SalesLead): void {
    this.selectedLead = lead;

    // Get detailed lead information
    this.salesDashboardService.getLeadDetails(lead.id).subscribe({
      next: (detailedLead) => {
        this.selectedLead = detailedLead;
        // Show modal
        const modal = new (window as any).bootstrap.Modal(
          document.getElementById('leadDetailsModal'),
        );
        modal.show();
      },
      error: (error) => {
        console.error('Failed to load lead details:', error);
      },
    });
  }

  /**
   * Send follow-up email
   */
  sendFollowUpEmail(lead: SalesLead): void {
    // Determine appropriate email template based on lead status and priority
    let templateType: 'welcome' | 'demo' | 'trial' | 'custom' = 'welcome';

    if (lead.status === 'NEW') {
      templateType = 'welcome';
    } else if (lead.status === 'CONTACTED') {
      templateType = 'demo';
    } else if (lead.priority === 'HIGH') {
      templateType = 'trial';
    }

    this.salesDashboardService
      .sendFollowUpEmail(lead.id, templateType)
      .subscribe({
        next: (_result) => {
          // TODO: Show success toast

          // Update lead status to CONTACTED if it was NEW
          if (lead.status === 'NEW') {
            this.updateLeadStatus(lead, 'CONTACTED', new Event('click'));
          }
        },
        error: (error) => {
          console.error('Failed to send follow-up email:', error);
          // TODO: Show error toast
        },
      });
  }

  /**
   * Add note to lead
   */
  addNote(lead: SalesLead): void {
    const note = prompt('Add a note for this lead:');
    if (note && note.trim()) {
      this.salesDashboardService.addLeadNote(lead.id, note.trim()).subscribe({
        next: (updatedLead) => {
          // Update local lead data
          const index = this.leads.findIndex((l) => l.id === lead.id);
          if (index !== -1) {
            this.leads[index] = updatedLead;
          }
          // TODO: Show success toast
        },
        error: (error) => {
          console.error('Failed to add note:', error);
          // TODO: Show error toast
        },
      });
    }
  }

  /**
   * Track by function for ngFor
   */
  trackByLeadId(index: number, lead: SalesLead): string {
    return lead.id;
  }

  /**
   * Get priority icon
   */
  getPriorityIcon(priority: string): string {
    switch (priority) {
      case 'HIGH':
        return '🔥';
      case 'MEDIUM':
        return '⚡';
      case 'NORMAL':
        return '📋';
      default:
        return '📋';
    }
  }

  /**
   * Format registration path for display
   */
  formatRegistrationPath(path: string): string {
    return path.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  /**
   * Get lead priority class
   */
  getLeadPriorityClass(lead: SalesLead): string {
    const baseClass = 'lead-priority';
    return `${baseClass} ${baseClass}-${lead.priority.toLowerCase()}`;
  }

  /**
   * Calculate urgency indicator
   */
  getUrgencyIndicator(lead: SalesLead): string {
    const hoursAgo =
      (new Date().getTime() - new Date(lead.registrationDate).getTime()) /
      (1000 * 60 * 60);

    if (lead.priority === 'HIGH' && hoursAgo > 2) {
      return 'urgent';
    } else if (lead.priority === 'MEDIUM' && hoursAgo > 24) {
      return 'overdue';
    } else if (hoursAgo > 72) {
      return 'stale';
    }

    return 'normal';
  }
}
